import { useCallback, useEffect, useMemo, useState } from 'react';

// Hooks
import useSubscriptionClick from '../../../../../packages/hooks/useSubscriptionClick';
import useFetchIntervalData from '../../../../../packages/hooks/useFetchIntervalData';

// Utils
import { getTeamStatistic } from '../../../../../packages/services/football/team';
import { normalizeTeam } from '../../normalizers/team/normalizeTeamResponseData';
import { getLabelValue, toSentenceCase } from '@sportal365/smp-widget-utils';

// Types
import { IFootballTeamH2hWidgetConfig, SDKOptions } from '../../../../../packages/types';
import { IGeneralSubscriberInfo } from '../../../../../packages/constants/subscribeTypes';
import { INormalizeTeam } from '../../types';

// Constants
import { TEAM_H2H_STATISTICS } from '../../../../../packages/constants/h2hStatistics';

interface IUseTeamComparisonArgs extends IGeneralSubscriberInfo {
  dataRefreshTime?: IFootballTeamH2hWidgetConfig['dataRefreshTime'];
  dataWidgetId?: IFootballTeamH2hWidgetConfig['dataWidgetId'];
  dataWidgetSport?: IFootballTeamH2hWidgetConfig['dataWidgetSport'];
  dataWidgetType?: IFootballTeamH2hWidgetConfig['dataWidgetType'];
  dataSportEntityOne?: IFootballTeamH2hWidgetConfig['dataSportEntityOne'];
  dataSportEntityTwo?: IFootballTeamH2hWidgetConfig['dataSportEntityTwo'];
  dataElements?: IFootballTeamH2hWidgetConfig['dataElements'];
  dataEntityLinks?: IFootballTeamH2hWidgetConfig['dataEntityLinks'];
  dataLabels?: IFootballTeamH2hWidgetConfig['dataLabels'];
  sdkOptions: SDKOptions;
}

export default function useTeamsComparison({
  dataRefreshTime,
  dataWidgetId,
  dataWidgetSport,
  subscribers,
  dataWidgetCuid,
  dataSportEntityOne,
  dataSportEntityTwo,
  dataElements,
  dataEntityLinks,
  dataLabels,
  sdkOptions,
}: IUseTeamComparisonArgs) {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [teamOne, setTeamOne] = useState<INormalizeTeam>();
  const [teamTwo, setTeamTwo] = useState<INormalizeTeam>();

  const fetchTeamsComparison = useCallback(
    (manageLoader: boolean) => {
      manageLoader && setLoading(true);
      if (dataSportEntityOne && dataSportEntityTwo) {
        getTeamStatistic({
          team_ids: [dataSportEntityOne.id, dataSportEntityTwo.id],
          season_ids: [dataSportEntityOne.seasonId, dataSportEntityTwo.seasonId],
          sdkOptions,
        })
          .then((response) => {
            manageLoader && setLoading(false);
            const teamOneData = normalizeTeam({
              data: response && response.length > 0 ? response[0] : undefined,
              dataEntityLinks,
            });
            const teamTwoData = normalizeTeam({
              data: response && response.length > 0 ? response[1] : undefined,
              dataEntityLinks,
            });
            setTeamOne(teamOneData);
            setTeamTwo(teamTwoData);
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      }
    },
    [dataSportEntityOne, dataSportEntityTwo],
  );

  useFetchIntervalData(fetchTeamsComparison, dataRefreshTime);

  useEffect(() => {
    fetchTeamsComparison(true);
  }, [fetchTeamsComparison]);

  const statisticsComparison = useMemo(() => {
    if (!teamOne?.statistics || !teamTwo?.statistics) return [];

    const statisticsToShow = dataElements?.length ? dataElements : TEAM_H2H_STATISTICS;

    return teamOne.statistics.reduce<{ name: string; label: string; valueOne: number; valueTwo: number }[]>((acc, { label, value }) => {
      if (!statisticsToShow?.includes(label)) return acc;

      const valueTwo = teamTwo.statistics.find((entity) => entity.label === label)?.value ?? 0;

      acc.push({
        name: label,
        label: getLabelValue(dataLabels?.[label], toSentenceCase(label)),
        valueOne: value,
        valueTwo: valueTwo,
      });

      return acc;
    }, []);
  }, [teamOne?.statistics, teamTwo?.statistics, dataElements, dataLabels]);

  return {
    widgetData: { teamOne, teamTwo, statisticsComparison, isLoading },
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
  };
}
